import { Component, ErrorInfo, PropsWithChildren } from "react";
import { SWRConfig } from "swr";
import { FetchError } from "lib/actions/fetcher";
import { datadogLogs } from "@datadog/browser-logs";
import { ErrorBoundaryCause } from "./ErrorBoundaryCause";
import UnhandledExceptionError from "./UnhandledExceptionError";
import Project403AccessError from "./Project403AccessError";
import Custom404Page from "pages/404";
import Workspace402SubscriptionRequiredError from "./Workspace402SubscriptionRequiredError";

// Unfortunately, this has to be a class component.
// https://nextjs.org/docs/pages/building-your-application/configuring/error-handling#handling-client-errors
// https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary

type State = {
  error: Error | null;
};

type Http402ResponsePayload = {
  reason: string;
  workspaceId: number;
};

class GlobalErrorBoundary extends Component<PropsWithChildren, State> {
  constructor(props: unknown) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    console.error(error);
    return { error };
  }

  reset() {
    this.setState({ error: null });
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error({ source: "GlobalErrorBoundary", error, errorInfo });
    datadogLogs.logger.error(
      "Unhandled exception caused application crash.",
      undefined,
      error,
    );
  }

  render() {
    if (this.state.error) {
      if (this.state.error.cause === ErrorBoundaryCause.ProjectAccessDenied) {
        return <Project403AccessError onReset={this.reset.bind(this)} />;
      } else if (
        this.state.error.cause === ErrorBoundaryCause.ProjectDoesNotExist
      ) {
        return <Custom404Page onReset={this.reset.bind(this)} />;
      } else if (
        this.state.error.cause === ErrorBoundaryCause.ValidSubscriptionRequired
      ) {
        const problemWorkspaceId = (
          (this.state.error as FetchError).response?.data as
            | Http402ResponsePayload
            | undefined
        )?.workspaceId;

        return (
          <Workspace402SubscriptionRequiredError
            onReset={this.reset.bind(this)}
            workspaceId={problemWorkspaceId}
          />
        );
      } else {
        return <UnhandledExceptionError onReset={this.reset.bind(this)} />;
      }
    }

    return (
      <SWRConfig
        value={{
          onError: (fetchError: FetchError) => {
            // Only the first error should display the full-screen error page.
            if (this.state.error) return;

            if (fetchError.status !== 402) return;

            this.setState({
              error: {
                ...fetchError,
                cause: ErrorBoundaryCause.ValidSubscriptionRequired,
              },
            });
          },
        }}
      >
        {this.props.children}
      </SWRConfig>
    );
  }
}

export default GlobalErrorBoundary;
